import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { UseFormReturn, useForm } from "react-hook-form";

import {
  getGeocode,
  useCustomFieldsAndGroups,
  useFeatureflags,
  useOrderFormConfig,
  useOrderMutation,
  useSavedAddressMutation,
} from "@web-app/api";
import {
  Button,
  countryCodeOptions,
  useAlert,
  validatePhone,
} from "@web-app/components";
import { useIsDesktop } from "@web-app/hooks";
import useDispatchStore from "@web-app/store/dispatchStore";
import {
  CustomField,
  CustomFieldValue,
  GoogleAddress,
  OrderDeliveryType,
  OrderPriorityType,
  SavedAddress,
  TransportType,
} from "@web-app/types";
import { getCurrencyForCountry } from "@web-app/utils/currency";
import {
  convertTimeAsTimezone,
  getTimezoneFromAddress,
} from "@web-app/utils/time";

import {
  extractCustomFieldId,
  isCustomField,
} from "../../settings/FormBuilderTab/constants";
import type { DetailsType } from "../../settings/FormBuilderTab/types";

import Form from "./components/Form";
import Map from "./components/Map";
import {
  getDefaultCustomFieldState,
  getRequiredCustomFieldErrors,
} from "./customFieldValidation";
import { getVisibleFormFields } from "./sectionVisibility";
import { CreateOrderDrawerProps, FormValues } from "./types";

export default function CreateOrderDrawer({
  isOpen,
  onClose,
}: CreateOrderDrawerProps) {
  const formRef = useRef<UseFormReturn<FormValues>>();
  const alert = useAlert();
  const [deliveryType, setDeliveryType] = useState<OrderDeliveryType>(
    OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF,
  );
  const [pickupLocation, setPickupLocation] = useState<GoogleAddress | null>(
    null,
  );
  const [dropoffLocation, setDropoffLocation] = useState<GoogleAddress | null>(
    null,
  );
  const [addressCountryCode, setAddressCountryCode] = useState<string>("");

  const isDesktop = useIsDesktop();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [directionsService, setDirectionsService] =
    useState<google.maps.DirectionsService | null>(null);
  const [directionsRenderer, setDirectionsRenderer] =
    useState<google.maps.DirectionsRenderer | null>(null);
  const [priority, setPriority] = useState<OrderPriorityType>(
    OrderPriorityType.LOW,
  );
  const [savePickupDetails, setSavePickupDetails] = useState(false);
  const [saveDropoffDetails, setSaveDropoffDetails] = useState(false);
  const [customFields, setCustomFields] = useState<
    Array<{ definition: CustomField; values: CustomFieldValue[] }>
  >([]);
  const [showCustomFieldErrors, setShowCustomFieldErrors] = useState(false);
  const [workspaceOptionType, setWorkspaceOptionType] = useState("createFor");

  const { user } = useDispatchStore();
  const currentWorkspaceId = user?.currentWorkspaceId || "";

  const { hasFeature } = useFeatureflags();
  const isFormBuilderEnabled = hasFeature("formBuilder");
  const { data: orderFormConfig } = useOrderFormConfig({
    enabled: isOpen && isFormBuilderEnabled,
  });
  const customFieldQueries = useCustomFieldsAndGroups({
    enabled: isOpen && isFormBuilderEnabled,
  });
  const customFieldDefinitionsData = customFieldQueries?.[0]?.data;
  const customFieldDefinitions = useMemo(
    () => customFieldDefinitionsData ?? [],
    [customFieldDefinitionsData],
  );

  const visibleFormFields = useMemo(
    () =>
      isFormBuilderEnabled
        ? getVisibleFormFields(orderFormConfig?.fields ?? [], deliveryType)
        : [],
    [isFormBuilderEnabled, orderFormConfig?.fields, deliveryType],
  );
  useEffect(() => {
    if (!isOpen || !isFormBuilderEnabled) return;
    setCustomFields((prev) => {
      const missing = getDefaultCustomFieldState(
        visibleFormFields,
        customFieldDefinitions,
      ).filter((d) => !prev.some((p) => p.definition.id === d.definition.id));
      return missing.length ? [...prev, ...missing] : prev;
    });
  }, [isOpen, isFormBuilderEnabled, visibleFormFields, customFieldDefinitions]);

  const requiredCustomFieldErrors = useMemo(
    () =>
      isFormBuilderEnabled
        ? getRequiredCustomFieldErrors(
            visibleFormFields,
            customFieldDefinitions,
            customFields,
          )
        : {},
    [
      isFormBuilderEnabled,
      visibleFormFields,
      customFieldDefinitions,
      customFields,
    ],
  );
  const customFieldErrors = showCustomFieldErrors
    ? requiredCustomFieldErrors
    : {};
  const isLoaded = true; // API provider handles loading
  const { createOrder } = useOrderMutation();
  const { createSavedAddress } = useSavedAddressMutation();

  const isIndividualDelivery =
    deliveryType === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF;
  const isPickupDelivery = deliveryType === OrderDeliveryType.ROUTE_OPT_PICKUP;

  const now = new Date();
  const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);

  const form = useForm<FormValues>({
    defaultValues: {
      connectedWorkspaceId: "",
      currency: "USD",
      description: "",
      disableSMS: false,
      dropOffBusinessName: "",
      dropoffLocation: null,
      dropOffName: "",
      dropOffNote: "",
      dropoffOrderRequirements_ITEMCHECKLIST: false,
      dropoffOrderRequirements_NOTES: false,
      dropoffOrderRequirements_PHOTO: false,
      dropoffOrderRequirements_SIGNATURE: false,
      dropOffPhone: "",
      dropOffServiceTime: "",
      dropOffStartTime: now,
      externalId: "",
      itemsCount: "0",
      pickupBusinessName: "",
      pickupChecklist: [],
      pickupEndTime: oneHourFromNow,
      pickupLocation: null,
      pickupName: "",
      pickupNote: "",
      pickupOrderRequirements_ITEMCHECKLIST: false,
      pickupOrderRequirements_NOTES: false,
      pickupOrderRequirements_PHOTO: false,
      pickupOrderRequirements_SIGNATURE: false,
      pickupPhone: "",
      pickupServiceTime: "",
      priority: OrderPriorityType.LOW,
      saveDropoffDetails: false,
      savePickupDetails: false,
      scheduledEndDateTime: oneHourFromNow,
      scheduledStartDateTime: now,
      timezone: "America/New_York",
      tip: "0",
      value: "0",
      vehicleType: TransportType.Sedan,
      workspaceId: currentWorkspaceId,
    },
    mode: "all",
    reValidateMode: "onChange",
    shouldFocusError: false,
  });

  const {
    control,
    formState,
    getValues,
    register,
    reset,
    setError,
    setFocus,
    setValue,
    trigger,
    watch,
  } = form;

  // Assign the form instance to the ref
  useEffect(() => {
    if (formRef?.current) {
      formRef.current = form;
    }
  }, [form, register, setFocus, formRef]);

  // Watch all form fields
  const formValues = watch();

  // Function to check if all required fields in a tab are filled
  const hasRequiredCustomFields = (detailsType: DetailsType) =>
    !visibleFormFields.some(
      (f) =>
        f.detailsType === detailsType &&
        isCustomField(f.fieldKey) &&
        requiredCustomFieldErrors[extractCustomFieldId(f.fieldKey)],
    );

  const isTabComplete = (tabName: string) => {
    switch (tabName) {
      case "Pickup Details":
        // For pickup details, check all required fields based on delivery type
        if (
          deliveryType === OrderDeliveryType.ROUTE_OPT_PICKUP ||
          deliveryType === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF
        ) {
          // Check if pickup location is valid
          const hasValidPickupLocation = !!(
            pickupLocation &&
            pickupLocation.geometry &&
            pickupLocation.geometry.location
          );

          // Check required fields based on delivery type
          const hasRequiredFields = !!(
            formValues.pickupName && formValues.pickupPhone
          );

          return (
            hasValidPickupLocation &&
            hasRequiredFields &&
            hasRequiredCustomFields("pickup")
          );
        }
        return false;
      case "Drop-off Details":
        // For dropoff details, check all required fields based on delivery type
        if (
          deliveryType === OrderDeliveryType.ROUTE_OPT_DROPOFF ||
          deliveryType === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF
        ) {
          // Check if dropoff location is valid
          const hasValidDropoffLocation = !!(
            dropoffLocation &&
            dropoffLocation.geometry &&
            dropoffLocation.geometry.location
          );

          // Check required fields based on delivery type
          const hasRequiredFields = !!(
            formValues.dropOffName && formValues.dropOffPhone
          );

          return (
            hasValidDropoffLocation &&
            hasRequiredFields &&
            hasRequiredCustomFields("dropoff")
          );
        }
        return false;
      case "Order Details":
        // For order details, check all required fields
        return !!formValues.vehicleType && hasRequiredCustomFields("order");
      case "Additional Details":
        return true; // Additional details are optional
      default:
        return false;
    }
  };

  // Auto-select currency and phone country when address country changes.
  // Lives here (not in tab components) so it only fires on actual address changes,
  // not on tab switches which unmount/remount the tab components.
  useEffect(() => {
    if (addressCountryCode) {
      const newCurrency = getCurrencyForCountry(addressCountryCode);
      setValue("currency", newCurrency);

      // Set phone country prefix if phone fields are empty
      const countryOption =
        countryCodeOptions[
          addressCountryCode.toLowerCase() as keyof typeof countryCodeOptions
        ];
      if (countryOption) {
        if (!getValues("pickupPhone")) {
          setValue("pickupPhone", countryOption.code);
        }
        if (!getValues("dropOffPhone")) {
          setValue("dropOffPhone", countryOption.code);
        }
      }
    }
  }, [addressCountryCode, setValue, getValues]);

  // Initialize directions service and renderer when map is loaded
  useEffect(() => {
    if (map && isLoaded) {
      const service = new google.maps.DirectionsService();
      const renderer = new google.maps.DirectionsRenderer({
        map,
        polylineOptions: {
          strokeColor: "#1F2937",
          strokeWeight: 3,
        },
        suppressMarkers: true,
      });
      setDirectionsService(service);
      setDirectionsRenderer(renderer);
    }
  }, [map, isLoaded]);

  const handleAddressSelect = async (
    address: GoogleAddress | SavedAddress | null,
    isPickup: boolean = true,
  ) => {
    if (!isLoaded) return;

    let finalAddressCoordinates;
    if (address) {
      if ("addressLocation" in address) {
        // It's a SavedAddress
        const googleAddress: GoogleAddress = {
          description: address.address,
          geometry: {
            location: {
              lat: address.addressLocation.coordinates[1],
              lng: address.addressLocation.coordinates[0],
            },
          },
          matched_substrings: [],
          place_id: address.id,
          reference: "",
          structured_formatting: {
            main_text: address.name,
            main_text_matched_substrings: [],
            secondary_text: address.address,
          },
          terms: [],
          types: [],
        };

        if (isPickup) {
          setPickupLocation(googleAddress);
          setValue("pickupLocation", googleAddress, {
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue("pickupName", address.name, {
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue("pickupPhone", address.phone, {
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue("pickupBusinessName", address.businessName, {
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue("pickupNote", address.note, {
            shouldTouch: true,
            shouldValidate: true,
          });
        } else {
          setDropoffLocation(googleAddress);
          setValue("dropoffLocation", googleAddress, {
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue("dropOffName", address.name, {
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue("dropOffPhone", address.phone, {
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue("dropOffBusinessName", address.businessName, {
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue("dropOffNote", address.note, {
            shouldTouch: true,
            shouldValidate: true,
          });
        }

        finalAddressCoordinates = {
          lat: address.addressLocation.coordinates[1],
          lng: address.addressLocation.coordinates[0],
        };
      } else {
        // It's a GoogleAddress
        const geocode = await getGeocode(address.description);
        const location = {
          ...address,
          geometry: {
            location: {
              lat: geocode.lat,
              lng: geocode.lng,
            },
          },
        };

        if (isPickup) {
          setPickupLocation(location);
          setValue("pickupLocation", location, { shouldTouch: true });
        } else {
          setDropoffLocation(location);
          setValue("dropoffLocation", location, { shouldTouch: true });
        }

        if (geocode.country) {
          setAddressCountryCode(geocode.country);
        }

        finalAddressCoordinates = {
          lat: geocode.lat,
          lng: geocode.lng,
        };
      }

      const tz = await getTimezoneFromAddress(finalAddressCoordinates);
      setValue("timezone", tz.timeZoneId);
    } else {
      if (isPickup) {
        setPickupLocation(null);
        setValue("pickupLocation", null, { shouldTouch: true });
      } else {
        setDropoffLocation(null);
        setValue("dropoffLocation", null, { shouldTouch: true });
      }
    }
  };

  // Reset all state to initial values
  const resetAllState = () => {
    setDeliveryType(OrderDeliveryType.ROUTE_OPT_PICKUP);
    setPickupLocation(null);
    setDropoffLocation(null);
    setAddressCountryCode("");
    setMap(null);
    setDirectionsService(null);
    setDirectionsRenderer(null);
    setPriority(OrderPriorityType.LOW);
    setSavePickupDetails(false);
    setSaveDropoffDetails(false);
    setCustomFields([]);
    setShowCustomFieldErrors(false);
    setWorkspaceOptionType("createFor");
    reset();
  };

  // Handle close with state reset
  const handleClose = () => {
    resetAllState();
    onClose();
  };

  const handleDeliveryTypeChange = (value: OrderDeliveryType) => {
    setDeliveryType(value);
    setPickupLocation(null);
    setDropoffLocation(null);
    if (isFormBuilderEnabled) {
      setCustomFields([]);
      setShowCustomFieldErrors(false);
    }
    reset();
  };

  const transformCustomFields = () => {
    return customFields.map((field) => ({
      definition: {
        defaultValues: (field.definition.defaultValues ?? []).map((v) => ({
          isSelected: v.isSelected,
          value: String(v.value),
        })),
        helpText: field.definition.helpText,
        id: field.definition.id,
        isActive: field.definition.isActive,
        isMulti: field.definition.isMulti,
        isRequired: field.definition.isRequired,
        isVisibleAddressBook: field.definition.isVisibleAddressBook,
        isVisibleMobile: field.definition.isVisibleMobile,
        isVisibleOrder: field.definition.isVisibleOrder,
        name: field.definition.name,
        type: field.definition.type,
      },
      values: field.values.map((value) => ({
        isSelected: value.isSelected,
        value: String(value.value),
      })),
    }));
  };

  const generateRouteOptDropoffOrder = (data: FormValues) => ({
    assigneeId: null,
    connectedWorkspaceId: data.connectedWorkspaceId,
    customFields: transformCustomFields(),
    details: {
      currency: data.currency || "USD",
      description: data.description,
      height: parseFloat(data.height || "0.00"),
      itemsCount: parseInt(data.itemsCount),
      length: parseFloat(data.length || "0.00"),
      serviceDuration: parseInt(data.dropOffServiceTime) || 0,
      tip: parseFloat(data.tip),
      value: parseFloat(data.value),
      weight: parseFloat(data.weight || "0.00"),
      width: parseFloat(data.width || "0.00"),
    },
    disableSMS: data.disableSMS,
    dropoff: {
      address: dropoffLocation?.description || "",
      businessName: data.dropOffBusinessName,
      name: data.dropOffName,
      note: data.dropOffNote || "",
      phone: data.dropOffPhone,
    },
    dropoffLocation: {
      coordinates: [
        dropoffLocation?.geometry?.location.lng || 0,
        dropoffLocation?.geometry?.location.lat || 0,
      ],
      type: "POINT",
    },
    dropoffRequirements: {
      itemChecklist: data.dropoffOrderRequirements_ITEMCHECKLIST,
      note: data.dropoffOrderRequirements_NOTES,
      photo: data.dropoffOrderRequirements_PHOTO,
      signature: data.dropoffOrderRequirements_SIGNATURE,
    },
    externalId: data.externalId,
    minVehicle: (data.vehicleType as TransportType) || TransportType.Sedan,
    pickup: {
      address: "",
      businessName: "",
      name: "",
      note: "",
      phone: "",
    },
    priority,
    scheduledEndDateTime: convertTimeAsTimezone(
      data.scheduledEndDateTime,
      data.timezone,
    ),
    scheduledStartDateTime: convertTimeAsTimezone(
      data.dropOffStartTime,
      data.timezone,
    ),
    status: null,
    timezone: data.timezone,
    type: OrderDeliveryType.ROUTE_OPT_DROPOFF,
    workspaceId: data.workspaceId,
  });

  const generateRouteOptPickupOrder = (data: FormValues) => ({
    assigneeId: null,
    connectedWorkspaceId: data.connectedWorkspaceId,
    customFields: transformCustomFields(),
    details: {
      currency: data.currency || "USD",
      description: data.description,
      height: parseFloat(data.height || "0.00"),
      itemsCount: parseInt(data.itemsCount),
      length: parseFloat(data.length || "0.00"),
      serviceDuration: parseInt(data.pickupServiceTime) || 0,
      tip: parseFloat(data.tip),
      value: parseFloat(data.value),
      weight: parseFloat(data.weight || "0.00"),
      width: parseFloat(data.width || "0.00"),
    },
    disableSMS: data.disableSMS,
    dropoff: {
      address: "",
      businessName: "",
      name: "",
      note: "",
      phone: "",
    },
    externalId: data.externalId,
    minVehicle: (data.vehicleType as TransportType) || TransportType.Sedan,
    pickup: {
      address: pickupLocation?.description || "",
      businessName: data.pickupBusinessName,
      name: data.pickupName,
      note: data.pickupNote || "",
      phone: data.pickupPhone,
    },
    pickupLocation: {
      coordinates: [
        pickupLocation?.geometry?.location.lng || 0,
        pickupLocation?.geometry?.location.lat || 0,
      ],
      type: "POINT",
    },
    pickupRequirements: {
      itemChecklist: data.pickupOrderRequirements_ITEMCHECKLIST,
      note: data.pickupOrderRequirements_NOTES,
      photo: data.pickupOrderRequirements_PHOTO,
      signature: data.pickupOrderRequirements_SIGNATURE,
    },
    priority,
    scheduledEndDateTime: convertTimeAsTimezone(
      data.pickupEndTime,
      data.timezone,
    ),
    scheduledStartDateTime: convertTimeAsTimezone(
      data.scheduledStartDateTime,
      data.timezone,
    ),
    status: null,
    timezone: data.timezone,
    type: OrderDeliveryType.ROUTE_OPT_PICKUP,
    workspaceId: data.workspaceId,
  });

  const generateIndividualOrder = (data: FormValues) => ({
    assigneeId: null,
    connectedWorkspaceId: data.connectedWorkspaceId,
    customFields: transformCustomFields(),
    details: {
      currency: data.currency || "USD",
      description: data.description,
      height: parseFloat(data.height || "0.00"),
      itemsCount: parseInt(data.itemsCount),
      length: parseFloat(data.length || "0.00"),
      serviceDuration: parseInt(data.dropOffServiceTime) || 0,
      tip: parseFloat(data.tip),
      value: parseFloat(data.value),
      weight: parseFloat(data.weight || "0.00"),
      width: parseFloat(data.width || "0.00"),
    },
    disableSMS: data.disableSMS,
    dropoff: {
      address: dropoffLocation?.description || "",
      businessName: data.dropOffBusinessName,
      name: data.dropOffName,
      note: data.dropOffNote || "",
      phone: data.dropOffPhone,
    },
    dropoffChecklist: data.dropoffChecklist || [],
    dropoffLocation: {
      coordinates: [
        dropoffLocation?.geometry?.location.lng || 0,
        dropoffLocation?.geometry?.location.lat || 0,
      ],
      type: "POINT",
    },
    dropoffRequirements: {
      itemChecklist: data.dropoffOrderRequirements_ITEMCHECKLIST,
      note: data.dropoffOrderRequirements_NOTES,
      photo: data.dropoffOrderRequirements_PHOTO,
      signature: data.dropoffOrderRequirements_SIGNATURE,
    },
    externalId: data.externalId,
    minVehicle: (data.vehicleType as TransportType) || TransportType.Sedan,
    pickup: {
      address: pickupLocation?.description || "",
      businessName: data.pickupBusinessName,
      name: data.pickupName,
      note: data.pickupNote || "",
      phone: data.pickupPhone,
    },
    pickupChecklist: data.pickupChecklist || [],
    pickupLocation: {
      coordinates: [
        pickupLocation?.geometry?.location.lng || 0,
        pickupLocation?.geometry?.location.lat || 0,
      ],
      type: "POINT",
    },
    pickupRequirements: {
      itemChecklist: data.pickupOrderRequirements_ITEMCHECKLIST,
      note: data.pickupOrderRequirements_NOTES,
      photo: data.pickupOrderRequirements_PHOTO,
      signature: data.pickupOrderRequirements_SIGNATURE,
    },
    priority,
    scheduledEndDateTime: convertTimeAsTimezone(
      data.scheduledEndDateTime,
      data.timezone,
    ),
    scheduledStartDateTime: convertTimeAsTimezone(
      data.scheduledStartDateTime,
      data.timezone,
    ),
    status: null,
    timezone: data.timezone,
    type: OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF,
    workspaceId: data.workspaceId,
  });

  const getValidationErrors = (formData: FormValues) => {
    const validationErrors: string[] = [];

    const updateValidationErrors = (
      fieldName: keyof FormValues,
      message: string,
      defaultValue?: FormValues[keyof FormValues],
    ) => {
      validationErrors.push(message);
      setError(fieldName, { message });
      setValue(fieldName, defaultValue ?? null, { shouldTouch: true });
    };

    if (deliveryType === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF) {
      // validate pickup location
      if (!pickupLocation?.geometry?.location) {
        updateValidationErrors("pickupLocation", "Pickup location is required");
      }

      // validate dropoff location
      if (!dropoffLocation?.geometry?.location) {
        updateValidationErrors(
          "dropoffLocation",
          "Drop-off location is required",
        );
      }

      // validate pickup phone number
      if (!validatePhone(formData.pickupPhone)) {
        updateValidationErrors(
          "pickupPhone",
          "Please enter a valid phone number (e.g. +1234567890)",
        );
      }

      // validate dropoff phone number
      if (!validatePhone(formData.dropOffPhone)) {
        updateValidationErrors(
          "dropOffPhone",
          "Please enter a valid phone number (e.g. +1234567890)",
        );
      }

      // validate pickup and dropoff times
      if (
        formData.scheduledEndDateTime.getTime() <=
        formData.scheduledStartDateTime.getTime()
      ) {
        updateValidationErrors(
          "scheduledEndDateTime",
          "Please ensure 'Drop-off At' is later than 'Pickup At'",
          formData.scheduledEndDateTime,
        );
      }
    } else if (deliveryType === OrderDeliveryType.ROUTE_OPT_PICKUP) {
      // validate pickup location
      if (!pickupLocation?.geometry?.location) {
        updateValidationErrors("pickupLocation", "Pickup location is required");
      }

      // validate pickup time
      if (
        formData.pickupEndTime.getTime() <=
        formData.scheduledStartDateTime.getTime()
      ) {
        updateValidationErrors(
          "pickupEndTime",
          "Please ensure 'Pickup Before' is later than 'Pickup After'",
          oneHourFromNow,
        );
      }

      // validate pickup phone number
      if (!validatePhone(formData.pickupPhone)) {
        updateValidationErrors(
          "pickupPhone",
          "Please enter a valid phone number (e.g. +1234567890)",
        );
      }
    } else if (deliveryType === OrderDeliveryType.ROUTE_OPT_DROPOFF) {
      // validate dropoff location
      if (!dropoffLocation?.geometry?.location) {
        updateValidationErrors(
          "dropoffLocation",
          "Drop-off location is required",
        );
      }

      // validate dropoff time
      if (
        formData.scheduledEndDateTime.getTime() <=
        formData.dropOffStartTime.getTime()
      ) {
        updateValidationErrors(
          "scheduledEndDateTime",
          "'Drop-off Before (Date/Time)' should be greater than 'Drop-off After (Date/Time)'",
          oneHourFromNow,
        );
      }

      // validate dropoff phone number
      if (!validatePhone(formData.dropOffPhone)) {
        updateValidationErrors(
          "dropOffPhone",
          "Please enter a valid phone number (e.g. +1234567890)",
        );
      }
    }

    return validationErrors;
  };

  const saveAddressDetails = async (formData: FormValues, isPickup = true) => {
    const addressToSave = {
      address:
        (isPickup ? formData.pickupLocation : formData.dropoffLocation)
          ?.description ?? "",
      addressLocation: {
        latitude: (isPickup ? pickupLocation : dropoffLocation)!.geometry!
          .location.lat,
        longitude: (isPickup ? pickupLocation : dropoffLocation)!.geometry!
          .location.lng,
      },
      businessName: isPickup
        ? formData.pickupBusinessName
        : formData.dropOffBusinessName,
      name: isPickup ? formData.pickupName : formData.dropOffName,
      note: isPickup ? formData.pickupNote : formData.dropOffNote,
      phone: isPickup ? formData.pickupPhone : formData.dropOffPhone,
      workspaceId: user?.currentWorkspaceId ?? "",
    };

    const addressOf = isPickup ? "pickup" : "drop-off";
    await alert.showAlertAsync(createSavedAddress.mutateAsync(addressToSave), {
      error: {
        message: `Failed to save ${addressOf} address. Please try again or contact support`,
        title: "Error",
        type: "error",
      },
      loading: {
        message: `Saving ${addressOf} address ...`,
        title: "Saving",
        type: "default",
      },
      success: {
        message: `Successfully saved ${addressOf} address`,
        title: "Success",
        type: "success",
      },
    });
  };

  const handleSave = async () => {
    // Trigger form validation for all fields
    const isValid = await trigger();

    setShowCustomFieldErrors(true);
    const hasCustomFieldErrors =
      Object.keys(requiredCustomFieldErrors).length > 0;

    if (!isValid || hasCustomFieldErrors) {
      // Mark all fields with errors as touched to show validation errors
      const fieldsWithErrors = Object.keys(formState.errors) as Array<
        keyof FormValues
      >;
      fieldsWithErrors.forEach((fieldName) => {
        const currentValue = getValues(fieldName);
        setValue(fieldName, currentValue, { shouldTouch: true });
      });

      // Force validation of all fields regardless of visibility
      const allFields = [
        "pickupName",
        "pickupPhone",
        "pickupLocation",
        "pickupBusinessName",
        "pickupServiceTime",
        "scheduledStartDateTime",
        "pickupEndTime",
        "dropOffName",
        "dropOffPhone",
        "dropoffLocation",
        "dropOffBusinessName",
        "dropOffServiceTime",
        "dropOffStartTime",
        "scheduledEndDateTime",
        "description",
        "value",
        "itemsCount",
        "vehicleType",
        "priority",
      ];

      // Mark all fields as touched to ensure they're validated
      allFields.forEach((fieldName) => {
        const currentValue = getValues(fieldName as keyof FormValues);
        setValue(fieldName as keyof FormValues, currentValue, {
          shouldTouch: true,
        });
      });

      // Trigger validation again to ensure all errors are shown
      await trigger();

      return;
    }

    let formData = getValues();

    let workspaceId = formData.workspaceId;
    let connectedWorkspaceId = null;

    if (formData.connectedWorkspaceId) {
      if (workspaceOptionType === "sendTo") {
        connectedWorkspaceId = formData.connectedWorkspaceId;
      } else {
        connectedWorkspaceId = currentWorkspaceId;
        workspaceId = formData.connectedWorkspaceId;
      }
    }

    formData = { ...formData, connectedWorkspaceId, workspaceId };

    // Check if all required fields are filled based on delivery type
    const errors = getValidationErrors(formData);
    if (errors.length) {
      return;
    }

    if (savePickupDetails) {
      await saveAddressDetails(formData);
    }

    if (saveDropoffDetails) {
      await saveAddressDetails(formData, false);
    }

    // If validation passes, proceed with saving
    try {
      const order = isIndividualDelivery
        ? generateIndividualOrder(formData)
        : isPickupDelivery
          ? generateRouteOptPickupOrder(formData)
          : generateRouteOptDropoffOrder(formData);

      await alert.showAlertAsync(createOrder.mutateAsync(order), {
        error: {
          message:
            "Failed to create order. Please try again or contact support",
          title: "Error",
          type: "error",
        },
        loading: {
          message: "Please wait...",
          title: "Creating Order",
          type: "default",
        },
        success: {
          message: "Successfully created order",
          title: "Success",
          type: "success",
        },
      });

      // Reset all state and close drawer
      handleClose();
    } catch (error) {
      console.error("Error creating order:", error);
      alert.showAlert({
        message: "Failed to create order. Please try again or contact support",
        title: "Error",
        type: "error",
      });
    }
  };

  if (!isLoaded) return null;

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="dispatch-createOrder relative z-50"
        onClose={handleClose}
      >
        {isDesktop ? (
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
        ) : (
          <></>
        )}

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel
                  className={`pointer-events-auto h-[100dvh] max-h-screen ${
                    pickupLocation?.geometry?.location ||
                    dropoffLocation?.geometry?.location
                      ? "w-[100vw]"
                      : "w-[100vw] md:w-[70vw] lg:w-[40vw]"
                  } max-w-full`}
                >
                  <div className="flex h-full flex-col bg-white shadow-xl">
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                      <div
                        className={`grid min-h-0 flex-1 ${
                          pickupLocation?.geometry?.location ||
                          dropoffLocation?.geometry?.location
                            ? "grid-cols-1 lg:grid-cols-[1fr,40vw]"
                            : "grid-cols-1"
                        }`}
                      >
                        {/* Map Section */}
                        {(pickupLocation?.geometry?.location ||
                          dropoffLocation?.geometry?.location) &&
                          isDesktop && (
                            <Map
                              pickupLocation={pickupLocation}
                              dropoffLocation={dropoffLocation}
                              onMapLoad={setMap}
                              directionsService={directionsService}
                              directionsRenderer={directionsRenderer}
                              map={map}
                            />
                          )}

                        <div className="flex min-h-0 flex-col overflow-hidden">
                          {/* Form Section */}
                          <Form
                            className="min-h-0 flex-1 overflow-y-auto"
                            deliveryType={deliveryType}
                            pickupLocation={pickupLocation}
                            dropoffLocation={dropoffLocation}
                            priority={priority}
                            savePickupDetails={savePickupDetails}
                            saveDropoffDetails={saveDropoffDetails}
                            onDeliveryTypeChange={handleDeliveryTypeChange}
                            onAddressSelect={handleAddressSelect}
                            onPriorityChange={setPriority}
                            onSavePickupDetailsChange={setSavePickupDetails}
                            onSaveDropoffDetailsChange={setSaveDropoffDetails}
                            onClose={handleClose}
                            currentWorkspaceId={user?.currentWorkspaceId || ""}
                            setCustomFields={setCustomFields}
                            control={control}
                            isTabComplete={isTabComplete}
                            handleAddressSelect={handleAddressSelect}
                            customFieldsState={customFields}
                            customFieldErrors={customFieldErrors}
                            workspaceOptionType={workspaceOptionType}
                            setWorkspaceOptionType={setWorkspaceOptionType}
                          />

                          {/* Actions */}
                          <div className="flex-shrink-0 border-t border-gray-200 px-4 py-2 md:py-3 lg:py-5 sm:px-6">
                            <div className="flex justify-end space-x-3">
                              <Button plain onClick={handleClose}>
                                Cancel
                              </Button>
                              <Button onClick={handleSave}>Create</Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
